import { Alert, Box, Button, CircularProgress, Skeleton } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { useDispatch, useSelector } from 'react-redux';
import {
	getGpsrProductTypes,
	getGpsrStatus,
	getGpsrStatusIsLoad,
	getGpsrAdditionalInformation,
	getGpsrAdditionalInformationIsLoad,
	getGpsrChangeServiceIsLoad,
} from 'redux/reducers/gpsr/selectors';
import {
	confirmGpsrAdditionalInformationRequest,
	getGpsrAdditionalInformationRequest,
	getGpsrCancellationRequest,
	getGpsrProductTypesRequest,
	getGpsrStatusRequest,
} from 'redux/reducers/gpsr/reducer';
import { IGpsrTypesItem } from 'redux/reducers/gpsr/types';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router';
import { TTableStructure } from 'services/constants/tableStructureTypes';
import { IRequestWithPageSortFilterSearch } from 'services/constants/types';
import { getFiltersFromNavigateParams, getOrderByFromNavigateParams } from 'utils/FilterParams';
import { PATHS } from 'routes/constants';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { DateTime } from 'luxon';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { NoGpsrBanner } from './NoGpsrBanner';
import { gpsrAdditionalInformationStructure } from './gpsrAdditionalInformationStructure';
import { gpsrTypesTableStructure } from './tableStructure/gpsrTypesTableStructure';
import { NoGpsrProductsBanner } from './NoGpsrProductsBanner';
import { EasTableInfoPannel, TEasTableInfoPannelData } from './EasTableInfoPannel';
import { ChangeGpsrServiceDialog } from './ChangeGpsrServiceDialog';
import { GpsrWarningBanner } from './GpsrWarningBanner';
import {
	getDashboardServices,
	getDashboardServicesLoader,
} from 'redux/reducers/dashboard/selectors';

export const GpsrService: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const gpsrStatus = useSelector(getGpsrStatus);
	const gpsrStatusIsLoad = useSelector(getGpsrStatusIsLoad);
	const dashboardServices = useSelector(getDashboardServices);
	const dashboardServicesIsLoad = useSelector(getDashboardServicesLoader);
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const items = useSelector(getGpsrProductTypes);
	const gpsrAdditionalInformation = useSelector(getGpsrAdditionalInformation);
	const gpsrAdditionalInformationIsLoad = useSelector(getGpsrAdditionalInformationIsLoad);
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const [openChangeGpsrServiceDialog, setOpenChangeGpsrServiceDialog] = useState(false);
	const [openConfirmSubscriptionDetailsDialog, setOpenConfirmSubscriptionDetailsDialog] =
		useState(false);
	const gpsrChangeServiceIsLoad = useSelector(getGpsrChangeServiceIsLoad);
	const tableStructure = gpsrTypesTableStructure as TTableStructure<keyof IGpsrTypesItem>[];

	useEffect(
		() => {
			dispatch(getGpsrStatusRequest());
		},
		// eslint-disable-next-line
		[],
	);

	const getGpsrRequestParams = (): IRequestWithPageSortFilterSearch => ({
		pageNumber: page ? +page : 1,
		pageSize: PAGE_SIZE,
		searchString: String(navigateParams?.search ?? ''),
		filters: getFiltersFromNavigateParams(tableStructure, navigateParams),
		orderBy: getOrderByFromNavigateParams(tableStructure, navigateParams),
	});

	useEffect(() => {
		dispatch(getGpsrProductTypesRequest(getGpsrRequestParams()));
		dispatch(getGpsrAdditionalInformationRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleLink = (id: number) => {
		dispatch(saveUrl(window.location.pathname + window.location.search));
		navigate(`${PATHS.GPSR_SERVICE_PAGE}/${id}`);
	};

	const handleGpsrActions = (productTypeId: number, typeOfDialog: string) => {
		if (typeOfDialog === 'Public Page') {
			window.open(items?.data.find((item) => item.id === productTypeId)?.doCLink ?? '', '_blank');
			// gpsrSku?.product_type?.doCLink ?? '', '_blank');
		}
	};

	const handleCancelSubscription = () => {
		dispatch(
			getGpsrCancellationRequest({
				gpsrRequest: {
					...getGpsrRequestParams(),
				},
			}),
		);
	};

	const handleOpenCancelDialog = () => {
		setOpenCancelDialog(true);
	};

	const handleConfirmSubscriptionDetails = () => {
		dispatch(confirmGpsrAdditionalInformationRequest());
	};

	return (
		<>
			{!dashboardServicesIsLoad &&
				!dashboardServices?.gpsrEnabled &&
				dashboardServices?.gpsrServiceStatusName === 'Suspended' &&
				dashboardServices?.gpsrWarning && (
					<GpsrWarningBanner warning={dashboardServices?.gpsrWarning} />
				)}
			<Box sx={{ margin: '0px 10px 0 10px' }}>
				{gpsrStatusIsLoad && <Skeleton variant="rectangular" height={530} />}
				{!gpsrStatusIsLoad && !gpsrStatus?.enabled && <NoGpsrBanner />}
				{gpsrStatus?.enabled && ((items && items?.data?.length > 0) || navigateParams) && (
					<EasTableWithFilter<IGpsrTypesItem>
						tableStructure={gpsrTypesTableStructure as TTableStructure<keyof IGpsrTypesItem>[]}
						items={items}
						itemsIsLoading={gpsrStatusIsLoad}
						handleAction={handleGpsrActions}
						handleLink={handleLink}
						highlightedRow={null}
						showButtons={items?.data.map((item) => {
							return {
								'Public Page': item.status !== 'No Declaration',
							};
						})}
						filterChildren={
							<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
								{gpsrAdditionalInformation?.allowConfirmation && (
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={() => setOpenConfirmSubscriptionDetailsDialog(true)}
										disabled={!!gpsrChangeServiceIsLoad}
									>
										{gpsrChangeServiceIsLoad ? (
											<CircularProgress size={20} />
										) : (
											'Confirm Subscription Details'
										)}
									</Button>
								)}
								<Button
									variant="contained"
									color="primary"
									// onClick={() => navigate(PATHS.CHANGE_GPSR_SERVICE_PAGE)}
									size="small"
									onClick={() => setOpenChangeGpsrServiceDialog(true)}
								>
									Change GPSR Service
								</Button>

								{gpsrAdditionalInformation?.allowCancel && (
									<Button
										variant="contained"
										color="primary"
										onClick={handleOpenCancelDialog}
										size="small"
									>
										Disable auto-renewal
									</Button>
								)}
							</Box>
						}
						minWidth={1000}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<EasTableInfoPannel
								structure={gpsrAdditionalInformationStructure}
								data={gpsrAdditionalInformation as unknown as TEasTableInfoPannelData}
							/>
						</Box>
					</EasTableWithFilter>
				)}
				{!gpsrStatusIsLoad &&
					gpsrStatus?.enabled &&
					items?.data?.length === 0 &&
					!navigateParams && <NoGpsrProductsBanner />}
				<ConfirmationDialog
					open={openCancelDialog}
					setClose={() => setOpenCancelDialog(false)}
					handleConfirm={() => {
						handleCancelSubscription();
						setOpenCancelDialog(false);
					}}
					title="Disable auto-renewal of the Subscription"
					text="Are you sure you want to disable auto-renewal of your GPSR subscription?"
				/>
				<ChangeGpsrServiceDialog
					open={openChangeGpsrServiceDialog}
					setClose={() => setOpenChangeGpsrServiceDialog(false)}
				/>
				<ConfirmationDialog
					open={openConfirmSubscriptionDetailsDialog}
					setClose={() => setOpenConfirmSubscriptionDetailsDialog(false)}
					handleConfirm={() => {
						handleConfirmSubscriptionDetails();
						setOpenConfirmSubscriptionDetailsDialog(false);
					}}
					title="Confirm GPSR Subscription Details"
					text="Are you sure you want to confirm your GPSR subscription details?"
				/>
			</Box>
		</>
	);
};
